// src/ui/screens/chats/widgets/MessageBubble.jsx

import React, { useState, useRef, useEffect } from 'react';

const COLORS = {
  orange: '#FF8C08',
  incomingBubble: '#23262C',
  sentTick: '#9E9E9E',
  readTick: '#34B7F1',
  errorRed: '#FF5252',
};

const TAIL_W = 8;
const TAIL_H = 10;
const SWIPE_THRESHOLD = 64;
const LONG_PRESS_MS = 500;

// ── Format time ──────────────────────────────────────────────────────────────

const formatTime = (date) => {
  if (!date) return '';
  // Already a display string like "3:45 PM" (ChatScreen pre-formats it) — show as-is
  if (typeof date === 'string' && isNaN(new Date(date).getTime())) return date;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ── Format duration for audio ──────────────────────────────────────────────

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// ── Tick Icon ───────────────────────────────────────────────────────────────

const TickIcon = ({ status }) => {
  switch (status) {
    case 'sending':
      return (
        <svg className="w-3.5 h-3.5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
        </svg>
      );
    case 'sent':
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.sentTick} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'delivered':
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.sentTick} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M7 15l2 2 4-4" />
        </svg>
      );
    case 'read':
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.readTick} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M7 15l2 2 4-4" />
        </svg>
      );
    case 'failed':
      // Mirrors Flutter's Icons.error_outline_rounded (circle + exclamation), not an "X".
      return (
        <svg className="w-3.5 h-3.5" fill="none" stroke={COLORS.errorRed} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
          <line x1="12" y1="7.5" x2="12" y2="13" strokeWidth={2} strokeLinecap="round" />
          <circle cx="12" cy="16.2" r="0.9" fill={COLORS.errorRed} stroke="none" />
        </svg>
      );
    default:
      return null;
  }
};

// ── Tail (rendered inline beside the bubble, matching the Flutter Row layout) ──

const Tail = ({ color, incoming }) => {
  return (
    <div
      className="flex-shrink-0"
      style={{
        width: TAIL_W,
        height: TAIL_H,
        marginBottom: 0,
        backgroundColor: color,
        clipPath: incoming
          ? 'polygon(100% 0, 100% 100%, 0 100%)'
          : 'polygon(0 0, 0 100%, 100% 100%)',
      }}
    />
  );
};

// ── Swipe-to-reply icon (fades in near the edge as the user drags) ─────────

const SwipeReplyIcon = ({ isMine, progress }) => {
  const clamped = Math.min(Math.max(progress, 0), 1);
  return (
    <div
      className="absolute top-0 bottom-0 flex items-center pointer-events-none"
      style={{ [isMine ? 'right' : 'left']: 8, opacity: clamped }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center"
        style={{
          backgroundColor: `rgba(255,140,8,${0.15 + 0.15 * clamped})`,
          border: '1.2px solid rgba(255,140,8,0.5)',
        }}
      >
        <svg className="w-4.5 h-4.5" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l-7 7 7 7M2 12h15a5 5 0 005-5" />
        </svg>
      </div>
    </div>
  );
};

// ── Per-corner bubble radius (matches Flutter's flat corner on the tail side) ─

const getBubbleRadius = (isMine, showTail) => ({
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  borderBottomLeftRadius: isMine ? '8px' : showTail ? '0px' : '8px',
  borderBottomRightRadius: isMine ? (showTail ? '0px' : '8px') : '8px',
});

// ── Waveform Bar ────────────────────────────────────────────────────────────

const WAVEFORM_HEIGHTS = [5, 9, 14, 10, 6, 13, 18, 11, 7, 15, 9, 5, 16, 12, 8, 14, 10, 6, 11, 17, 8, 13, 9, 5, 12, 16, 10, 7, 14, 9];

const WaveformBar = ({ progress }) => {
  const total = WAVEFORM_HEIGHTS.length;
  const filled = Math.round(progress * total);

  return (
    <div className="flex items-center w-full h-5 gap-0.5">
      {WAVEFORM_HEIGHTS.map((height, i) => {
        const played = i < filled;
        return (
          <div
            key={i}
            className="flex-1 rounded-sm"
            style={{
              height,
              backgroundColor: played ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
            }}
          />
        );
      })}
    </div>
  );
};

// ── Draggable Waveform ─────────────────────────────────────────────────────

const DraggableWaveform = ({ progress, totalDuration, onSeek }) => {
  const [dragProgress, setDragProgress] = useState(null);
  const containerRef = useRef(null);

  const displayProgress = dragProgress !== null ? dragProgress : progress;

  const handleDragStart = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX || e.touches?.[0]?.clientX || 0) - rect.left;
    const p = Math.max(0, Math.min(1, x / rect.width));
    setDragProgress(p);
  };

  const handleDragMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const x = clientX - rect.left;
    const p = Math.max(0, Math.min(1, x / rect.width));
    setDragProgress(p);
  };

  const handleDragEnd = () => {
    if (dragProgress !== null) {
      const seekMs = Math.round(dragProgress * totalDuration);
      if (onSeek) onSeek(seekMs);
    }
    setDragProgress(null);
  };

  const handleClick = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const p = Math.max(0, Math.min(1, x / rect.width));
    const seekMs = Math.round(p * totalDuration);
    if (onSeek) onSeek(seekMs);
  };

  return (
    <div
      ref={containerRef}
      className="cursor-pointer"
      onMouseDown={(e) => { e.stopPropagation(); handleDragStart(e); }}
      onMouseMove={(e) => { if (dragProgress !== null) handleDragMove(e); }}
      onMouseUp={(e) => { e.stopPropagation(); handleDragEnd(); }}
      onMouseLeave={handleDragEnd}
      onTouchStart={(e) => { e.stopPropagation(); handleDragStart(e); }}
      onTouchMove={(e) => { e.preventDefault(); handleDragMove(e); }}
      onTouchEnd={(e) => { e.stopPropagation(); handleDragEnd(); }}
      onClick={(e) => { e.stopPropagation(); handleClick(e); }}
    >
      <WaveformBar progress={displayProgress} />
    </div>
  );
};

// ── Reply Preview ──────────────────────────────────────────────────────────

const ReplyPreview = ({
  replyToId,
  replyToContent,
  replyToSenderId,
  replyToType,
  replyToAttachmentUrl,
  isMine,
  currentUserId,
  senderName,
  onScrollToReply,
}) => {
  if (!replyToId) return null;

  const isMyReply = replyToSenderId === currentUserId;
  const authorLabel = isMyReply ? 'You' : (senderName || 'User');

  let typeIcon = null;
  if (replyToType === 'image') {
    typeIcon = (
      <svg className="w-3.5 h-3.5 text-white/55" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  } else if (replyToType === 'audio') {
    typeIcon = (
      <svg className="w-3.5 h-3.5 text-white/55" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onScrollToReply?.(replyToId); }}
      className="w-full px-2 pt-1.5 pb-1 flex items-start gap-1.5 border-l-3"
      style={{
        backgroundColor: isMine ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.07)',
        borderLeftColor: isMine ? 'rgba(255,255,255,0.6)' : COLORS.orange,
      }}
    >
      <div className="flex-1 min-w-0 text-left">
        <p className={`text-xs font-bold ${isMine ? 'text-white/85' : 'text-filatom-primary'}`}>
          {authorLabel}
        </p>
        <div className="flex items-center gap-1">
          {typeIcon}
          <p className="text-white/60 text-xs truncate">{replyToContent || ''}</p>
        </div>
      </div>
      {replyToType === 'image' && replyToAttachmentUrl && (
        <img
          src={replyToAttachmentUrl}
          alt="Reply thumbnail"
          className="w-9 h-9 rounded object-cover flex-shrink-0"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
    </button>
  );
};

// ── Forwarded Label ────────────────────────────────────────────────────────

const ForwardedLabel = ({ isMine, isForwarded }) => {
  if (!isForwarded) return null;
  return (
    <div className="flex items-center gap-1 px-2.5 pt-1.5">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isMine ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.4)' }}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
      </svg>
      <span className={`text-xs italic ${isMine ? 'text-white/55' : 'text-white/40'}`}>Forwarded</span>
    </div>
  );
};

// ── Image Gallery Dialog ───────────────────────────────────────────────────

const ImageGalleryDialog = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(Math.max(1, Math.min(4, scale + delta)));
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setStartPos({ x: touch.clientX, y: touch.clientY });
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      setPosition({
        x: position.x + (touch.clientX - startPos.x),
        y: position.y + (touch.clientY - startPos.y),
      });
      setStartPos({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"
      >
        <svg className="w-5.5 h-5.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-black/50">
          <span className="text-white text-sm">{currentIndex + 1} / {images.length}</span>
        </div>
      )}

      {/* Main image */}
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden"
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="transition-transform duration-200"
          style={{
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
          }}
        >
          {images[currentIndex]?.url ? (
            <img
              src={images[currentIndex].url}
              alt={`Image ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              draggable={false}
            />
          ) : images[currentIndex]?.localPath ? (
            <img
              src={images[currentIndex].localPath}
              alt={`Image ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              draggable={false}
            />
          ) : (
            <div className="w-20 h-20 text-white/20 flex items-center justify-center">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 px-4 overflow-x-auto">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => {
                setCurrentIndex(i);
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
              className={`w-12 h-12 rounded flex-shrink-0 overflow-hidden border-2 transition-all ${
                i === currentIndex ? 'border-filatom-primary' : 'border-transparent'
              }`}
            >
              {img.url ? (
                <img src={img.url} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
              ) : img.localPath ? (
                <img src={img.localPath} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-filatom-card" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Bubble shell: handles long-press / tap-to-select / swipe-to-reply and ───
// ── renders the tail beside the bubble (not stacked underneath it) ─────────

const BubbleShell = ({
  isMine,
  showTail,
  bubbleColor,
  dragOffset,
  gestureProps,
  isSelected,
  children,
}) => (
  <div className="relative" {...gestureProps} style={{ cursor: 'pointer' }}>
    {dragOffset > 0 && <SwipeReplyIcon isMine={isMine} progress={dragOffset / SWIPE_THRESHOLD} />}
    <div
      className={`flex items-end px-2.5 py-1 ${isMine ? 'justify-end' : 'justify-start'}`}
      style={{ transform: `translateX(${dragOffset}px)`, transition: dragOffset === 0 ? 'transform 180ms ease-out' : 'none' }}
    >
      {!isMine && showTail && <Tail color={bubbleColor} incoming={true} />}
      <div className="relative max-w-[75%]">
        {isSelected && (
          <div
            className="absolute -inset-1 rounded-lg pointer-events-none"
            style={{ backgroundColor: 'rgba(255,140,8,0.12)' }}
          />
        )}
        {children}
      </div>
      {isMine && showTail && <Tail color={bubbleColor} incoming={false} />}
    </div>
  </div>
);

// ── MessageBubble ──────────────────────────────────────────────────────────

export const MessageBubble = ({
  content,
  time,
  isMine,
  showTail = true,
  status = null,
  isDeleted = false,
  messageType = 'text',
  attachmentUrl = null,
  localFilePath = null,
  audioDuration = null,
  isForwarded = false,
  replyToId = null,
  replyToContent = null,
  replyToSenderId = null,
  replyToType = null,
  replyToAttachmentUrl = null,
  imageUrls = null,
  imageLocalPaths = null,
  isSelected = false,
  isSelectionMode = false,
  onLongPress = null,
  onTap = null,
  onSwipeReply = null,
  onScrollToReply = null,
  currentUserId = null,
  senderName = null,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [totalDuration, setTotalDuration] = useState(audioDuration || 0);
  const [dragOffset, setDragOffset] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const audioRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const replyTriggeredRef = useRef(false);
  const longPressFiredRef = useRef(false);

  // ── Audio player ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (messageType === 'audio' && attachmentUrl) {
      const audio = new Audio(attachmentUrl);
      audioRef.current = audio;

      audio.addEventListener('timeupdate', () => {
        setPlaybackPosition(audio.currentTime);
      });

      audio.addEventListener('loadedmetadata', () => {
        setTotalDuration(audio.duration);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setPlaybackPosition(0);
        audio.currentTime = 0;
      });

      return () => {
        audio.pause();
        audioRef.current = null;
      };
    }
  }, [messageType, attachmentUrl]);

  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const seekAudio = (seconds) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = seconds;
    setPlaybackPosition(seconds);
  };

  // ── Gesture handling: long-press-to-select, tap-to-select, swipe-to-reply ──
  // Mirrors the Flutter GestureDetector wrapping the whole bubble.

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const getPoint = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handlePointerDown = (e) => {
    if (isSelectionMode) return;
    touchStartRef.current = getPoint(e);
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      if (navigator.vibrate) navigator.vibrate(10);
      onLongPress?.();
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e) => {
    if (isSelectionMode) return;
    const { x, y } = getPoint(e);
    const dx = x - touchStartRef.current.x;
    const dy = y - touchStartRef.current.y;

    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
      clearLongPressTimer();
    }

    // Only rightward drags register, matching the Flutter clamp(0, threshold*1.2).
    if (dx > 0 && Math.abs(dy) < 40) {
      const newOffset = Math.min(dx, SWIPE_THRESHOLD * 1.2);
      setDragOffset(newOffset);
      if (newOffset >= SWIPE_THRESHOLD && !replyTriggeredRef.current) {
        replyTriggeredRef.current = true;
        if (navigator.vibrate) navigator.vibrate(5);
      } else if (newOffset < SWIPE_THRESHOLD) {
        replyTriggeredRef.current = false;
      }
    }
  };

  const handlePointerUp = () => {
    clearLongPressTimer();
    if (replyTriggeredRef.current) {
      onSwipeReply?.();
    }
    setDragOffset(0);
    replyTriggeredRef.current = false;
  };

  const handleContainerClick = () => {
    if (longPressFiredRef.current) {
      // The long-press already handled this interaction; swallow the trailing click.
      longPressFiredRef.current = false;
      return;
    }
    if (isSelectionMode) {
      onTap?.();
    }
  };

  const gestureProps = {
    onMouseDown: handlePointerDown,
    onMouseMove: (e) => { if (e.buttons === 1) handlePointerMove(e); },
    onMouseUp: handlePointerUp,
    onMouseLeave: () => { clearLongPressTimer(); setDragOffset(0); replyTriggeredRef.current = false; },
    onTouchStart: handlePointerDown,
    onTouchMove: handlePointerMove,
    onTouchEnd: handlePointerUp,
    onClick: handleContainerClick,
  };

  // Guards nested interactive elements (image thumbnails, audio play button,
  // reply-preview) so that selection mode intercepts taps instead of firing
  // their normal action — mirrors expected chat-app UX during multi-select.
  const guardedAction = (action) => (...args) => {
    if (isSelectionMode) {
      onTap?.();
      return;
    }
    action?.(...args);
  };

  // ── Build gallery items ─────────────────────────────────────────────────

  const getGalleryImages = () => {
    if (imageUrls && imageUrls.length > 0) {
      return imageUrls.map((url) => ({ url, localPath: null }));
    }
    if (imageLocalPaths && imageLocalPaths.length > 0) {
      return imageLocalPaths.map((path) => ({ url: null, localPath: path }));
    }
    if (attachmentUrl) {
      return [{ url: attachmentUrl, localPath: localFilePath || null }];
    }
    return [];
  };

  const openGallery = guardedAction((index) => {
    const images = getGalleryImages();
    if (images.length === 0) return;
    setGalleryIndex(index);
    setShowGallery(true);
  });

  // ── Check if content is emoji-only ─────────────────────────────────────

  const isEmojiOnly = () => {
    if (!content) return false;
    const noEmoji = content.replace(
      /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]|\ufe0f|\u200d)+/g,
      ''
    );
    return noEmoji.trim().length === 0;
  };

  // ── Render time row ─────────────────────────────────────────────────────

  const renderTimeRow = () => {
    return (
      <div className="flex items-center gap-0.5">
        <span className={`text-[11px] leading-none ${isMine ? 'text-white/75' : 'text-white/50'}`}>
          {formatTime(time)}
        </span>
        {isMine && status && <TickIcon status={status} />}
      </div>
    );
  };

  // ── Deleted ──────────────────────────────────────────────────────────────

  if (isDeleted) {
    const bubbleColor = isMine ? COLORS.orange : COLORS.incomingBubble;
    return (
      <>
        <BubbleShell
          isMine={isMine}
          showTail={showTail}
          bubbleColor={bubbleColor}
          dragOffset={dragOffset}
          gestureProps={gestureProps}
          isSelected={isSelected}
        >
          <div
            className="flex items-center gap-1.5 px-2.5 py-1.5"
            style={{ backgroundColor: bubbleColor, ...getBubbleRadius(isMine, showTail) }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isMine ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.35)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <span className={`text-sm italic ${isMine ? 'text-white/55' : 'text-white/40'}`}>
              This message was deleted
            </span>
            {renderTimeRow()}
          </div>
        </BubbleShell>
      </>
    );
  }

  // ── Image ────────────────────────────────────────────────────────────────

  if (messageType === 'image') {
    const images = getGalleryImages();
    const hasCaption = content && content !== 'Photo';
    const bubbleColor = isMine ? COLORS.orange : COLORS.incomingBubble;

    const renderGrid = () => {
      if (images.length === 1) {
        return (
          <button onClick={(e) => { e.stopPropagation(); openGallery(0); }} className="w-[240px] h-[280px] overflow-hidden block">
            {(images[0].url || images[0].localPath) && (
              <img src={images[0].url || images[0].localPath} alt="Message" className="w-full h-full object-cover" />
            )}
          </button>
        );
      }

      if (images.length === 2) {
        return (
          <div className="flex gap-0.5">
            <button onClick={(e) => { e.stopPropagation(); openGallery(0); }} className="flex-1 h-[180px] overflow-hidden">
              <img src={images[0].url || images[0].localPath} alt="Image" className="w-full h-full object-cover" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); openGallery(1); }} className="flex-1 h-[180px] overflow-hidden">
              <img src={images[1].url || images[1].localPath} alt="Image" className="w-full h-full object-cover" />
            </button>
          </div>
        );
      }

      if (images.length === 3) {
        return (
          <div className="flex gap-0.5">
            <button onClick={(e) => { e.stopPropagation(); openGallery(0); }} className="flex-1 h-[220px] overflow-hidden">
              <img src={images[0].url || images[0].localPath} alt="Image" className="w-full h-full object-cover" />
            </button>
            <div className="flex-1 flex flex-col gap-0.5">
              <button onClick={(e) => { e.stopPropagation(); openGallery(1); }} className="h-[109px] overflow-hidden">
                <img src={images[1].url || images[1].localPath} alt="Image" className="w-full h-full object-cover" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); openGallery(2); }} className="h-[109px] overflow-hidden">
                <img src={images[2].url || images[2].localPath} alt="Image" className="w-full h-full object-cover" />
              </button>
            </div>
          </div>
        );
      }

      // 4+ images
      const show = images.slice(0, 4);
      const overflow = images.length - 4;
      return (
        <div className="flex flex-col gap-0.5">
          <div className="flex gap-0.5">
            <button onClick={(e) => { e.stopPropagation(); openGallery(0); }} className="flex-1 h-[130px] overflow-hidden">
              <img src={show[0].url || show[0].localPath} alt="Image" className="w-full h-full object-cover" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); openGallery(1); }} className="flex-1 h-[130px] overflow-hidden">
              <img src={show[1].url || show[1].localPath} alt="Image" className="w-full h-full object-cover" />
            </button>
          </div>
          <div className="flex gap-0.5">
            <button onClick={(e) => { e.stopPropagation(); openGallery(2); }} className="flex-1 h-[130px] overflow-hidden">
              <img src={show[2].url || show[2].localPath} alt="Image" className="w-full h-full object-cover" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); openGallery(3); }} className="flex-1 h-[130px] overflow-hidden relative">
              <img src={show[3].url || show[3].localPath} alt="Image" className="w-full h-full object-cover" />
              {overflow > 0 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-xl font-bold">+{overflow}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      );
    };

    return (
      <>
        <BubbleShell
          isMine={isMine}
          showTail={showTail}
          bubbleColor={bubbleColor}
          dragOffset={dragOffset}
          gestureProps={gestureProps}
          isSelected={isSelected}
        >
          {replyToId && (
            <ReplyPreview
              replyToId={replyToId}
              replyToContent={replyToContent}
              replyToSenderId={replyToSenderId}
              replyToType={replyToType}
              replyToAttachmentUrl={replyToAttachmentUrl}
              isMine={isMine}
              currentUserId={currentUserId}
              senderName={senderName}
              onScrollToReply={guardedAction(onScrollToReply)}
            />
          )}
          <ForwardedLabel isMine={isMine} isForwarded={isForwarded} />
          <div
            className="overflow-hidden"
            style={{ backgroundColor: bubbleColor, ...getBubbleRadius(isMine, showTail) }}
          >
            {renderGrid()}
            <div className={`flex items-center gap-1 px-2 py-1 ${hasCaption ? 'justify-between' : 'justify-end'}`}>
              {hasCaption && (
                <span className="text-white text-xs flex-1">{content}</span>
              )}
              {renderTimeRow()}
            </div>
          </div>
        </BubbleShell>

        {showGallery && (
          <ImageGalleryDialog
            images={getGalleryImages()}
            initialIndex={galleryIndex}
            onClose={() => setShowGallery(false)}
          />
        )}
      </>
    );
  }

  // ── Audio ────────────────────────────────────────────────────────────────

  if (messageType === 'audio') {
    const duration = totalDuration || audioDuration || 0;
    const progress = duration > 0 ? playbackPosition / duration : 0;
    const bubbleColor = isMine ? COLORS.orange : COLORS.incomingBubble;

    return (
      <BubbleShell
        isMine={isMine}
        showTail={showTail}
        bubbleColor={bubbleColor}
        dragOffset={dragOffset}
        gestureProps={gestureProps}
        isSelected={isSelected}
      >
        <div className="w-[230px]">
          {replyToId && (
            <ReplyPreview
              replyToId={replyToId}
              replyToContent={replyToContent}
              replyToSenderId={replyToSenderId}
              replyToType={replyToType}
              replyToAttachmentUrl={replyToAttachmentUrl}
              isMine={isMine}
              currentUserId={currentUserId}
              senderName={senderName}
              onScrollToReply={guardedAction(onScrollToReply)}
            />
          )}
          <ForwardedLabel isMine={isMine} isForwarded={isForwarded} />
          <div
            className="px-3 py-2"
            style={{ backgroundColor: bubbleColor, ...getBubbleRadius(isMine, showTail) }}
          >
            <div className="flex items-center gap-2.5">
              <button
                onClick={(e) => { e.stopPropagation(); guardedAction(togglePlayback)(); }}
                className="w-10.5 h-10.5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: isMine ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
                }}
              >
                <svg className="w-6.5 h-6.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  {isPlaying ? (
                    <>
                      <path d="M6 4h4v16H6z" />
                      <path d="M14 4h4v16h-4z" />
                    </>
                  ) : (
                    <path d="M8 5v14l11-7z" />
                  )}
                </svg>
              </button>
              <div className="flex-1">
                <DraggableWaveform
                  progress={progress}
                  totalDuration={duration}
                  onSeek={guardedAction(seekAudio)}
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-[11px] ${isMine ? 'text-white/75' : 'text-white/55'}`}>
                    {isPlaying || playbackPosition > 0
                      ? formatDuration(playbackPosition)
                      : formatDuration(duration)}
                  </span>
                  {renderTimeRow()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </BubbleShell>
    );
  }

  // ── Text ─────────────────────────────────────────────────────────────────

  const isEmoji = isEmojiOnly();
  const hasReplyOrForward = !!replyToId || isForwarded;
  const bubbleColor = isMine ? COLORS.orange : COLORS.incomingBubble;

  if (isEmoji && !hasReplyOrForward) {
    return (
      <BubbleShell
        isMine={isMine}
        showTail={false}
        bubbleColor="transparent"
        dragOffset={dragOffset}
        gestureProps={gestureProps}
        isSelected={isSelected}
      >
        <div className="text-center">
          <span className="text-4xl leading-relaxed">{content}</span>
          <div className={`flex mt-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
            {renderTimeRow()}
          </div>
        </div>
      </BubbleShell>
    );
  }

  return (
    <BubbleShell
      isMine={isMine}
      showTail={showTail}
      bubbleColor={bubbleColor}
      dragOffset={dragOffset}
      gestureProps={gestureProps}
      isSelected={isSelected}
    >
      <div
        className="px-2.5 py-1.5"
        style={{ backgroundColor: bubbleColor, ...getBubbleRadius(isMine, showTail) }}
      >
        {replyToId && (
          <ReplyPreview
            replyToId={replyToId}
            replyToContent={replyToContent}
            replyToSenderId={replyToSenderId}
            replyToType={replyToType}
            replyToAttachmentUrl={replyToAttachmentUrl}
            isMine={isMine}
            currentUserId={currentUserId}
            senderName={senderName}
            onScrollToReply={guardedAction(onScrollToReply)}
          />
        )}
        <ForwardedLabel isMine={isMine} isForwarded={isForwarded} />
        <span className="text-white text-sm leading-relaxed whitespace-pre-wrap break-words">
          {content}
        </span>
        <div className="flex justify-end mt-0.5">
          {renderTimeRow()}
        </div>
      </div>
    </BubbleShell>
  );
};

export default MessageBubble;